import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Eraser, Undo2, Keyboard, PenLine } from 'lucide-react';
import { BUTTONS, RADIUS } from '../core/tokens';
import { useIsMobile } from './useMediaQuery';
import {
  PAD_W, PAD_H, LINE_W, TYPED_BASE_PX, SCRIPT_FONT,
  startStroke, extendStroke, undoStroke, clearStrokes, toPadPoint, hasEnoughInk, cropRect, exportLayout,
  smoothPath, fitFontPx, typedLayout, typedReady, stripDataUrl,
} from './signaturePadLogic';

// The signature pad (docs/signature-field-scoping-2026-10.md §2.3). Pointer Events on one <canvas> cover mouse, pen and touch; `touch-action: none`
// sits on the canvas ALONE (so the page still scrolls everywhere else); the buffer is sized by devicePixelRatio so lines are not blurry; the box
// is a fixed 3:1. No library: the maths is `signaturePadLogic.js` (pure, tested), this file is the drawing and the controls.
//
// THE ACCESSIBLE WAY IN is "Type instead": the signer's typed name is rendered in a script face into the same canvas and exported the same way
// (method "typed"), so a keyboard-only or screen-reader user can sign. Both modes produce a white-background PNG cropped to the ink.
//
// The parent owns the name box (it is the sheet's own field, and typed mode draws its value) and reads the result through a ref:
//   ref.current.exportPng() -> { image: <bare base64 PNG>, method } | null (null when nothing signable is there)
// `onChange({ method, ready })` fires whenever the mode or whether there is anything to sign changes.

const INK = '#0f172a';

function drawStrokes(ctx, strokes, scale, ox = 0, oy = 0) {
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.strokeStyle = INK;
  ctx.fillStyle = INK;
  ctx.lineWidth = LINE_W * scale;
  for (const stroke of strokes) {
    const p = smoothPath(stroke);
    if (!p) continue;
    const X = (v) => v * scale - ox;
    const Y = (v) => v * scale - oy;
    if (p.dot) {
      ctx.beginPath();
      ctx.arc(X(p.start.x), Y(p.start.y), (LINE_W * scale) / 2, 0, Math.PI * 2);
      ctx.fill();
      continue;
    }
    ctx.beginPath();
    ctx.moveTo(X(p.start.x), Y(p.start.y));
    for (const c of p.curves) ctx.quadraticCurveTo(X(c.cx), Y(c.cy), X(c.x), Y(c.y));
    ctx.lineTo(X(p.end.x), Y(p.end.y));
    ctx.stroke();
  }
}

const scriptFont = (px) => `italic ${Math.round(px)}px ${SCRIPT_FONT}`;

function drawTyped(ctx, name, w, h) {
  const text = String(name || '').trim();
  if (!text) return;
  ctx.font = scriptFont(TYPED_BASE_PX);
  const px = fitFontPx(ctx.measureText(text).width, w * 0.9, { maxPx: h * 0.55 });
  ctx.font = scriptFont(px);
  ctx.fillStyle = INK;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, w / 2, h / 2);
}

export const SignaturePad = forwardRef(function SignaturePad({ name = '', methods = ['drawn', 'typed'], onChange, disabled = false }, ref) {
  const canDraw = methods.includes('drawn');
  const canType = methods.includes('typed');
  const canvasRef = useRef(null);
  const strokesRef = useRef([]);
  const drawingRef = useRef(false);
  const isMobile = useIsMobile();
  const [mode, setMode] = useState(canDraw ? 'drawn' : 'typed');
  const [count, setCount] = useState(0);            // strokes, for Undo/Clear
  const [inked, setInked] = useState(false);        // enough ink to count
  const ready = mode === 'drawn' ? inked : typedReady(name);

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext?.('2d');
    if (!canvas || !ctx) return;
    const scale = canvas.width / PAD_W;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    if (mode === 'drawn') drawStrokes(ctx, strokesRef.current, scale);
    else drawTyped(ctx, name, canvas.width, canvas.height);
  }, [mode, name]);

  // Size the buffer to the on-screen box times devicePixelRatio, and again when the box changes (rotation, a window resize).
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const fit = () => {
      const dpr = (typeof window !== 'undefined' && window.devicePixelRatio) || 1;
      const cssW = canvas.clientWidth || PAD_W;
      canvas.width = Math.max(1, Math.round(cssW * dpr));
      canvas.height = Math.max(1, Math.round((cssW * dpr * PAD_H) / PAD_W));
      redraw();
    };
    fit();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(fit);
    ro.observe(canvas);
    return () => ro.disconnect();
  }, [redraw]);

  useEffect(() => { onChange?.({ method: mode, ready }); }, [mode, ready]); // eslint-disable-line react-hooks/exhaustive-deps

  const point = (e) => toPadPoint(e.clientX, e.clientY, canvasRef.current.getBoundingClientRect());
  const commit = (next) => {
    strokesRef.current = next;
    setCount(next.length);
    setInked(hasEnoughInk(next));
    redraw();
  };

  const onPointerDown = (e) => {
    if (disabled || mode !== 'drawn' || (e.pointerType === 'mouse' && e.button !== 0)) return;
    e.preventDefault();
    drawingRef.current = true;
    try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch { /* a synthetic pointer has nothing to capture */ }
    commit(startStroke(strokesRef.current, point(e)));
  };
  const onPointerMove = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    commit(extendStroke(strokesRef.current, point(e)));
  };
  const onPointerEnd = (e) => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    try { e.currentTarget.releasePointerCapture?.(e.pointerId); } catch { /* already released */ }
  };

  useImperativeHandle(ref, () => ({
    exportPng() {
      if (!ready) return null;
      const out = document.createElement('canvas');
      const ctx = out.getContext('2d');
      if (!ctx) return null;
      if (mode === 'drawn') {
        const rect = cropRect(strokesRef.current);
        const layout = exportLayout(rect);
        if (!layout) return null;
        out.width = layout.width; out.height = layout.height;
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, out.width, out.height);
        drawStrokes(ctx, strokesRef.current, layout.scale, rect.x * layout.scale, rect.y * layout.scale);
      } else {
        const text = String(name).trim();
        ctx.font = scriptFont(TYPED_BASE_PX);
        const layout = typedLayout(ctx.measureText(text).width);
        out.width = layout.width; out.height = layout.height;
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, out.width, out.height);
        ctx.font = scriptFont(layout.fontPx);
        ctx.fillStyle = INK;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(text, out.width / 2, out.height / 2);
      }
      return { image: stripDataUrl(out.toDataURL('image/png')), method: mode };
    },
  }), [ready, mode, name]);

  const btn = `inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 ${RADIUS.control} ${BUTTONS.secondary} disabled:opacity-40 disabled:cursor-not-allowed`;
  const label = mode === 'drawn'
    ? 'Signature pad. Draw your signature here with a finger, a pen or a mouse. To sign with the keyboard instead, use the Type instead button.'
    : 'Your typed name, shown as a signature.';

  return (
    <div>
      <div className={`relative overflow-hidden ${RADIUS.container} border border-neutral-300 dark:border-neutral-600 bg-white`}>
        <canvas
          ref={canvasRef}
          role="img"
          aria-label={label}
          data-testid="signature-canvas"
          className="block w-full bg-white"
          style={{ aspectRatio: '3 / 1', touchAction: mode === 'drawn' ? 'none' : 'auto', cursor: mode === 'drawn' && !disabled ? 'crosshair' : 'default' }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerEnd}
          onPointerCancel={onPointerEnd}
        />
        {mode === 'drawn' && count === 0 && (
          <span aria-hidden="true" className="pointer-events-none absolute inset-x-4 bottom-5 flex items-center gap-2 text-neutral-400 text-lg select-none">
            <span>×</span><span className="flex-1 border-b border-neutral-300" />
          </span>
        )}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {mode === 'drawn' && (
          <>
            <button type="button" className={btn} onClick={() => commit(undoStroke(strokesRef.current))} disabled={disabled || count === 0}>
              <Undo2 size={13} aria-hidden="true" /> Undo
            </button>
            <button type="button" className={btn} onClick={() => commit(clearStrokes())} disabled={disabled || count === 0}>
              <Eraser size={13} aria-hidden="true" /> Clear
            </button>
          </>
        )}
        {canDraw && canType && (
          <button type="button" className={`${btn} sm:ml-auto`} onClick={() => setMode((m) => (m === 'drawn' ? 'typed' : 'drawn'))} disabled={disabled}>
            {mode === 'drawn' ? <Keyboard size={13} aria-hidden="true" /> : <PenLine size={13} aria-hidden="true" />}
            {mode === 'drawn' ? 'Type instead' : 'Draw instead'}
          </button>
        )}
      </div>
      <p className="mt-2 text-[11px] text-subtle dark:text-muted">
        {mode === 'drawn'
          ? (isMobile ? 'Sign with your finger. Turning your phone sideways gives you more room.' : 'Sign with your mouse, pen or finger.')
          : (typedReady(name) ? 'Your typed name will be kept as your signature.' : 'Type your name in the box above and it will appear here.')}
      </p>
    </div>
  );
});
